import './editor.css'
import Bold from '@tiptap/extension-bold'
import { BubbleMenu } from '@tiptap/extension-bubble-menu'
import Document from '@tiptap/extension-document'
import Italic from '@tiptap/extension-italic'
import Mention from '@tiptap/extension-mention'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Strike from '@tiptap/extension-strike'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import { PluginKey } from '@tiptap/pm/state'
import {
  Content,
  Editor,
  EditorContent,
  JSONContent,
  ReactRenderer,
  useEditor,
  useEditorState,
} from '@tiptap/react'
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import { useHandleScroll, useMediaQuery } from '@sparkai/hooks'
import { cn } from '@sparkai/utils'

import { Avatar } from '../Avatar'
import { Button } from '../General/Button'
import { Icon, IconsName } from '../Icon'
import { Mention as MentionComponent } from './mention'

export type PersonProps = {
  name: string
  id: string
  avatarUrl?: string
}

export type PostPayloadProps = {
  userId: string
  content: Content
  commentParentId?: string
  mentionedUserIds: string[]
}

export type ReplyTo = {
  commentId: string
  userName: string
}

export type EditorTextBaseProps = {
  isPosting?: boolean
  user?: PersonProps
  mentions?: PersonProps[]
  replyingTo?: ReplyTo
  onPost?: (data: PostPayloadProps) => void
  onChange?: (content: Content) => void
  onCloseReply?: () => void
}

export type EditorTextProps = EditorTextBaseProps & {
  initialData: Content
  editable: boolean
  placeholder?: string
  truncateText?: boolean
  hasQuote?: boolean
  mentionsHidden?: boolean
  postButtonHidden?: boolean
  disableMobileExperience?: boolean
  avatarSize?: number
  smallInput?: boolean
}

export function EditorText({
  initialData,
  mentions,
  editable,
  placeholder,
  onChange,
  isPosting,
  replyingTo,
  user,
  onPost,
  onCloseReply,
  truncateText,
  hasQuote,
  mentionsHidden,
  postButtonHidden,
  disableMobileExperience,
  smallInput,
  avatarSize = 40,
}: Readonly<EditorTextProps>) {
  const [disabledButton, setDisabledButton] = useState(!initialData)
  const [inputPressed, setInputPressed] = useState(false)

  const { isMobile } = useMediaQuery()

  const isMobileEnabled = isMobile && !disableMobileExperience
  const bubbleMenuElement = useMemo(() => document.createElement('div'), [])
  const bubbleMenuPluginKey = useMemo(() => new PluginKey('bubbleMenuText'), [])
  const isEditable = editable && !isPosting

  const isMobileDarkOverlay = editable && isMobileEnabled && inputPressed
  useHandleScroll(isMobileDarkOverlay)

  function extractMentionIds(node: JSONContent): string[] {
    let ids: string[] = []

    if (node.type === 'mention' && node.attrs && node.attrs.id) ids.push(node.attrs.id)

    if (node.content && Array.isArray(node.content)) {
      for (const child of node.content) {
        ids = ids.concat(extractMentionIds(child))
      }
    }

    return ids
  }

  function getAllMentionIds(doc: JSONContent): string[] {
    if (doc.type !== 'doc' || !doc.content) return []

    let allIds: string[] = []
    for (const node of doc.content) allIds = allIds.concat(extractMentionIds(node))

    return allIds
  }

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Underline,
      Italic,
      Strike,
      Mention.configure({
        HTMLAttributes: { class: 'mentionNode text-blue-600 font-semibold' },
        suggestion: {
          render: () => {
            let reactRenderer: ReactRenderer

            return {
              onStart: (props) => {
                reactRenderer = new ReactRenderer(MentionComponent.List, {
                  props: { ...props, mentions: mentions ?? [], mentionsHidden },
                  editor: props.editor,
                })
              },

              onUpdate(props) {
                reactRenderer?.updateProps(props)
              },

              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  reactRenderer?.destroy()
                  return true
                }

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return (reactRenderer?.ref as any)?.onKeyDown(props)
              },

              onExit() {
                reactRenderer.destroy()
              },
            }
          },
        },
      }),
      Placeholder.configure({ placeholder: editable && !replyingTo ? placeholder : '' }),
      BubbleMenu.configure({
        pluginKey: bubbleMenuPluginKey,
        element: bubbleMenuElement,
        shouldShow: ({ editor, from, to }) => from !== to && editor.isEditable,
      }),
    ],
    content: initialData,
    editable: isEditable,
    editorProps: {
      attributes: {
        class: cn({
          'border-2 text-sm lg:text-base min-h-[105px] bg-white max-h-[105px] overflow-y-auto border-gray-300 py-[9px] px-[13px] rounded-md shadow-xs':
            editable,
          'min-h-[64px] max-h-[64px]': editable && smallInput,
          'pt-2 px-2 text-gray-700 text-sm font-normal': !editable,
          'line-clamp-1 pt-0 px-0 mention': truncateText,
          quote: hasQuote,
        }),
      },
    },
    onUpdate: ({ editor }) => {
      const contentJson = editor.state.doc.content.toJSON() as { type: string; content?: object[] }[]
      const hasData = contentJson.filter((x) => Boolean(x.content)).length > 0

      if (hasData) setDisabledButton(false)
      else setDisabledButton(true)

      onChange?.(editor.getJSON())
    },
  }) as Editor

  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor?.isActive('bold') ?? false,
      isItalic: editor?.isActive('italic') ?? false,
      isUnderline: editor?.isActive('underline') ?? false,
      isStrike: editor?.isActive('strike') ?? false,
    }),
  })

  useEffect(() => {
    if (!editor || editor.isEditable === isEditable) return

    editor.setEditable(isEditable, false)
    if (!isEditable) editor.view.dispatch(editor.state.tr.setMeta(bubbleMenuPluginKey, 'hide'))
  }, [editor, isEditable, bubbleMenuPluginKey])

  const onHandleEditorFocus = (isOpen: boolean) => {
    if (!editable || !isMobileEnabled) return

    setInputPressed(isOpen)
  }

  const handlePost = () => {
    if (!user) return

    const payload = editor.getJSON()
    const mentionedUserIds = getAllMentionIds(payload)

    onPost?.({
      userId: user.id,
      content: payload,
      commentParentId: replyingTo?.commentId,
      mentionedUserIds,
    })

    editor.commands.clearContent()
    setDisabledButton(true)
    setInputPressed(false)
  }

  const shouldChangeInputPosition = isMobileDarkOverlay || (isMobileEnabled && editable)

  if (!editor) return null

  return (
    <>
      <div
        data-testid='editor-text-section'
        className={cn('editor flex w-full gap-4 lg:gap-6', {
          'fixed bottom-0 left-0 z-[60] w-full border-t border-t-gray-200 bg-white px-4 pt-6 pb-9':
            shouldChangeInputPosition,
        })}
      >
        {createPortal(
          <div
            data-testid='editor-text-bubble-menu'
            className='flex max-w-fit gap-5 bg-white px-6 py-4 shadow-lg'
          >
            <RichTextButton
              iconName='bold'
              isActive={editorState.isBold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />

            <RichTextButton
              iconName='italic'
              isActive={editorState.isItalic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />

            <RichTextButton
              iconName='underline'
              isActive={editorState.isUnderline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />

            <RichTextButton
              iconName='strike-through'
              isActive={editorState.isStrike}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
          </div>,
          bubbleMenuElement
        )}

        {user && editable && (
          <Avatar
            fallback={{ value: user.name }}
            imageProps={{ src: user.avatarUrl }}
            width={avatarSize}
            height={avatarSize}
          />
        )}

        <div
          className={cn('editor-container relative flex w-full gap-4', {
            'lg:flex-col lg:gap-6': !smallInput,
          })}
        >
          <div className='flex w-full flex-col'>
            {replyingTo && (
              <div
                data-testid='editor-text-replyingTo'
                className={cn(
                  'absolute flex !w-[calc(100%-54px)] items-start justify-between rounded-md border-2 border-gray-300 bg-gray-50 py-2 pr-[10px] pl-3 lg:!w-full',
                  {
                    'lg:!w-[calc(100%-68px)]': smallInput,
                  }
                )}
              >
                <p className='text-xs font-normal text-gray-400'>
                  {`You're replying to ${replyingTo.userName}`}
                </p>
                <Button
                  data-testid='editor-text-reply-close'
                  variant='unstyled'
                  className='min-w-fit p-0 px-0 py-0'
                  onClick={() => {
                    setInputPressed(false)
                    onCloseReply?.()
                  }}
                >
                  <Icon name='close' className='h-2 w-2 text-gray-400' />
                </Button>
              </div>
            )}

            <div data-testid='editor-text-input' className={cn({ 'mt-[28px]': Boolean(replyingTo) })}>
              <EditorContent editor={editor} onClick={() => onHandleEditorFocus(true)} />
            </div>
          </div>

          {editable && user !== undefined && !postButtonHidden && (
            <div
              className={cn('flex max-w-fit justify-end', {
                'items-center lg:w-full lg:max-w-full': !smallInput,
                'items-end': smallInput,
              })}
            >
              <Button
                data-testid='post-comment-btn'
                className='flex h-[38px] w-[38px] gap-2 rounded-full bg-blue-500 px-[9px] py-[10px] lg:h-auto lg:w-fit lg:rounded-md lg:px-[15px] lg:py-[9px]'
                disabled={disabledButton || isPosting}
                onClick={handlePost}
              >
                {isPosting && (
                  <div className='flex !h-4 !w-4 animate-spin items-center justify-center rounded-full border-2 border-blue-100 border-t-white lg:h-5 lg:w-5' />
                )}

                {!isMobileEnabled && !smallInput && <>{isPosting ? 'Posting...' : 'Post Comment'}</>}

                {(isMobileEnabled || smallInput) && !isPosting && (
                  <Icon name='send' className='h-5 w-5 text-white' />
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
      {isMobileDarkOverlay && (
        <button
          type='button'
          data-testid='editor-text-dark-layer'
          onClick={() => onHandleEditorFocus(false)}
          className={cn('fixed top-[58px] left-0 z-30 h-full w-full bg-[#6B728040] lg:top-0')}
        />
      )}
    </>
  )
}

function RichTextButton({
  isActive,
  iconName,
  onClick,
}: Readonly<{
  isActive: boolean
  iconName: IconsName
  onClick: () => void
}>) {
  return (
    <button
      type='button'
      className={cn({
        'bg-gray-100 text-blue-500': isActive,
      })}
      onClick={onClick}
    >
      <Icon name={iconName} />
    </button>
  )
}
